// Mobile utilities for Capacitor native features
import { Capacitor } from '@capacitor/core';
import { StatusBar, Style } from '@capacitor/status-bar';
import { Haptics, ImpactStyle, NotificationType } from '@capacitor/haptics';
import { Keyboard } from '@capacitor/keyboard';
import { App } from '@capacitor/app';
import { Browser } from '@capacitor/browser';
import { Share } from '@capacitor/share';
import { Preferences } from '@capacitor/preferences';
import { Camera, CameraResultType, CameraSource } from '@capacitor/camera';

// Check if running as native app
export const isNative = Capacitor.isNativePlatform();
export const isIOS = Capacitor.getPlatform() === 'ios';
export const isAndroid = Capacitor.getPlatform() === 'android';
export const isWeb = Capacitor.getPlatform() === 'web';

// Initialize mobile-specific settings
export const initializeMobile = async () => {
  if (!isNative) return;

  try {
    // Set status bar style for dark theme
    await StatusBar.setStyle({ style: Style.Dark });
    
    if (isIOS) {
      await StatusBar.setBackgroundColor({ color: '#0a0a0a' });
    }

    // Listen for keyboard events
    Keyboard.addListener('keyboardWillShow', (info) => {
      document.body.style.setProperty('--keyboard-height', `${info.keyboardHeight}px`);
    });

    Keyboard.addListener('keyboardWillHide', () => {
      document.body.style.setProperty('--keyboard-height', '0px');
    });

    // Handle back button on Android
    App.addListener('backButton', ({ canGoBack }) => {
      if (canGoBack) {
        window.history.back();
      } else {
        App.exitApp();
      }
    });

    // Handle app state changes
    App.addListener('appStateChange', ({ isActive }) => {
      console.log('App state changed. Is active:', isActive);
    });

    console.log('Mobile initialized successfully');
  } catch (error) {
    console.error('Error initializing mobile:', error);
  }
};

// Haptic feedback functions
export const haptics = {
  light: async () => {
    if (!isNative) return;
    await Haptics.impact({ style: ImpactStyle.Light });
  },
  medium: async () => {
    if (!isNative) return;
    await Haptics.impact({ style: ImpactStyle.Medium });
  },
  heavy: async () => {
    if (!isNative) return;
    await Haptics.impact({ style: ImpactStyle.Heavy });
  },
  success: async () => {
    if (!isNative) return;
    await Haptics.notification({ type: NotificationType.Success });
  },
  warning: async () => {
    if (!isNative) return;
    await Haptics.notification({ type: NotificationType.Warning });
  },
  error: async () => {
    if (!isNative) return;
    await Haptics.notification({ type: NotificationType.Error });
  },
  selection: async () => {
    if (!isNative) return;
    await Haptics.selectionStart();
    await Haptics.selectionChanged();
    await Haptics.selectionEnd();
  }
};

// Camera functions for profile photos
export const takePhoto = async () => {
  try {
    const image = await Camera.getPhoto({
      quality: 90,
      allowEditing: true,
      resultType: CameraResultType.Base64,
      source: CameraSource.Prompt, // Let user choose camera or gallery
      width: 500,
      height: 500,
    });
    return image;
  } catch (error) {
    console.error('Camera error:', error);
    throw error;
  }
};

// Open external links in browser
export const openBrowser = async (url) => {
  if (isNative) {
    await Browser.open({ url });
  } else {
    window.open(url, '_blank');
  }
};

// Share content
export const shareContent = async ({ title, text, url }) => {
  if (isNative) {
    await Share.share({ title, text, url });
  } else if (navigator.share) {
    await navigator.share({ title, text, url });
  } else {
    // Fallback: copy to clipboard
    await navigator.clipboard.writeText(url || text);
    alert('Link copied to clipboard!');
  }
};

// Local storage using Capacitor Preferences (persists across app updates)
export const storage = {
  set: async (key, value) => {
    await Preferences.set({ key, value: JSON.stringify(value) });
  },
  get: async (key) => {
    const { value } = await Preferences.get({ key });
    return value ? JSON.parse(value) : null;
  },
  remove: async (key) => {
    await Preferences.remove({ key });
  },
  clear: async () => {
    await Preferences.clear();
  }
};

// Safe area insets for notch handling
export const getSafeAreaInsets = () => {
  const computedStyle = getComputedStyle(document.documentElement);
  return {
    top: parseInt(computedStyle.getPropertyValue('--sat') || '0', 10),
    bottom: parseInt(computedStyle.getPropertyValue('--sab') || '0', 10),
    left: parseInt(computedStyle.getPropertyValue('--sal') || '0', 10),
    right: parseInt(computedStyle.getPropertyValue('--sar') || '0', 10),
  };
};

export default {
  isNative,
  isIOS,
  isAndroid,
  isWeb,
  initializeMobile,
  haptics,
  takePhoto,
  openBrowser,
  shareContent,
  storage,
  getSafeAreaInsets,
};
